/**
 * Department Mapping — one-shot page for HR/Admin to map employees to departments
 * and set HODs. Replaces the piecemeal Departments + Employees editing flow.
 *
 * Left panel:  list of existing departments (with HOD chip) + "Create new" inline.
 * Right panel: employee table with a per-row Department picker (or "+ New" inline).
 * HOD row:     for each department, a dropdown of employees in it → picks the HOD.
 * Save Changes: single button submits everything via POST /api/departments/bulk-assign.
 *
 * Access: super_admin, admin, hr, institute_admin, branch_admin.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
  Users, Plus, Save, Loader2, Search, ShieldCheck, Building2, ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_BASE as API_URL } from '../lib/apiBase';
import { safeJson } from '../lib/safeJson';

const headers = () => {
  const t = localStorage.getItem('hrms-token');
  return t ? { 'Authorization': `Bearer ${t}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
};

const CREATE_NEW = '__CREATE_NEW__';

export default function DepartmentMapping() {
  const { selectedEntity } = useOutletContext() || {};
  const { user } = useAuth();
  const navigate = useNavigate();
  const role = (user?.role || '').toLowerCase();
  const canManage = ['super_admin', 'admin', 'hr', 'institute_admin', 'branch_admin'].includes(role);

  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [filterDept, setFilterDept] = useState('all');

  // Local edits (only sent when "Save Changes" is clicked)
  const [pendingAssignments, setPendingAssignments] = useState({}); // employee_id -> new dept name
  const [pendingNewDepts, setPendingNewDepts] = useState([]);        // [{name, code}]
  const [pendingHods, setPendingHods] = useState({});                // department name -> employee_id
  const [newDeptDraft, setNewDeptDraft] = useState({ name: '', code: '' });

  const entityId = selectedEntity?.entity_id;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const empUrl = new URL(`${API_URL}/api/employees`);
      if (entityId) empUrl.searchParams.set('entity_id', entityId);
      const deptUrl = new URL(`${API_URL}/api/departments`);
      if (entityId) deptUrl.searchParams.set('entity_id', entityId);
      const [empRes, deptRes] = await Promise.all([
        fetch(empUrl, { headers: headers(), credentials: 'include' }),
        fetch(deptUrl, { headers: headers(), credentials: 'include' }),
      ]);
      const emps = await safeJson(empRes);
      const depts = await safeJson(deptRes);
      setEmployees(Array.isArray(emps) ? emps : []);
      setDepartments(Array.isArray(depts) ? depts : []);
      // Reset local edits when scope changes
      setPendingAssignments({});
      setPendingNewDepts([]);
      setPendingHods({});
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [entityId]);

  useEffect(() => { if (canManage) fetchData(); }, [fetchData, canManage]);

  // All available department names (existing + pending new)
  const allDeptNames = useMemo(() => {
    const s = new Set(departments.map(d => d.name).filter(Boolean));
    pendingNewDepts.forEach(d => s.add(d.name));
    return Array.from(s).sort();
  }, [departments, pendingNewDepts]);

  // What each employee's department WILL BE after save (pending or current)
  const effectiveDeptForEmp = (emp) =>
    pendingAssignments[emp.employee_id] ?? emp.department ?? '';

  // Filtered employee rows
  const visibleEmployees = useMemo(() => {
    const q = search.trim().toLowerCase();
    return employees.filter(e => {
      const name = `${e.first_name || ''} ${e.last_name || ''}`.toLowerCase();
      const matchesSearch = !q ||
        name.includes(q) ||
        (e.email || '').toLowerCase().includes(q) ||
        (e.employee_id || '').toLowerCase().includes(q);
      const dept = effectiveDeptForEmp(e);
      const matchesDept = filterDept === 'all' || dept === filterDept || (filterDept === '__none__' && !dept);
      return matchesSearch && matchesDept;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees, search, filterDept, pendingAssignments]);

  // Employees grouped by their effective department (for the HOD picker)
  const empsByDept = useMemo(() => {
    const m = {};
    for (const e of employees) {
      const d = effectiveDeptForEmp(e);
      if (!d) continue;
      (m[d] = m[d] || []).push(e);
    }
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees, pendingAssignments]);

  const onPickDepartment = (employee_id, value) => {
    if (value === CREATE_NEW) {
      const name = window.prompt('New department name?');
      if (!name?.trim()) return;
      const code = (window.prompt(`Short code for '${name}'? (optional)`, name.slice(0, 6).toUpperCase()) || '').toUpperCase();
      // Prevent duplicates
      if (allDeptNames.some(n => n.toLowerCase() === name.trim().toLowerCase())) {
        toast.error('That department already exists');
        return;
      }
      setPendingNewDepts(prev => [...prev, { name: name.trim(), code: code.trim() }]);
      setPendingAssignments(prev => ({ ...prev, [employee_id]: name.trim() }));
      toast.success(`Will create '${name.trim()}' on Save`);
      return;
    }
    setPendingAssignments(prev => ({ ...prev, [employee_id]: value }));
  };

  const onPickHod = (deptName, employee_id) => {
    if (employee_id === '__divider__') return;
    setPendingHods(prev => ({ ...prev, [deptName]: employee_id }));
  };

  const [actingDept, setActingDept] = useState(null);
  const [actingForm, setActingForm] = useState({ employee_id: '', from_date: '', to_date: '' });
  const [savingActing, setSavingActing] = useState(false);

  const saveActingHod = async () => {
    if (!actingForm.employee_id || !actingForm.to_date) { toast.error('Pick the acting HOD and an end date'); return; }
    setSavingActing(true);
    try {
      const res = await fetch(`${API_URL}/api/departments/${actingDept.department_id}/acting-hod`, {
        method: 'PUT', headers: headers(), credentials: 'include',
        body: JSON.stringify({ hod_employee_id: actingForm.employee_id, from_date: actingForm.from_date, to_date: actingForm.to_date })
      });
      const d = await safeJson(res);
      if (!res.ok) throw new Error(d.detail || 'Failed to set acting HOD');
      toast.success(d.message);
      setActingDept(null);
      fetchData();
    } catch (e) { toast.error(e.message); }
    finally { setSavingActing(false); }
  };

  const clearActingHod = async (dept) => {
    try {
      const res = await fetch(`${API_URL}/api/departments/${dept.department_id}/acting-hod`, {
        method: 'DELETE', headers: headers(), credentials: 'include'
      });
      const d = await safeJson(res);
      if (!res.ok) throw new Error(d.detail || 'Failed');
      toast.success(d.message);
      fetchData();
    } catch (e) { toast.error(e.message); }
  };

  const onAddDraftDept = () => {
    const n = (newDeptDraft.name || '').trim();
    if (!n) return;
    if (allDeptNames.some(d => d.toLowerCase() === n.toLowerCase())) {
      toast.error(`'${n}' already exists`);
      return;
    }
    setPendingNewDepts(prev => [...prev, { name: n, code: (newDeptDraft.code || '').trim().toUpperCase() }]);
    setNewDeptDraft({ name: '', code: '' });
    toast.success(`'${n}' queued for creation. Save Changes to apply.`);
  };

  const currentHodEmployeeId = (deptName) => {
    if (pendingHods[deptName] !== undefined) return pendingHods[deptName];
    const dept = departments.find(d => d.name === deptName);
    if (!dept?.hod_id) return '';
    const emp = employees.find(e => e.user_id === dept.hod_id || e.employee_id === dept.hod_id);
    return emp?.employee_id || '';
  };

  const totalPending = Object.keys(pendingAssignments).length + pendingNewDepts.length + Object.keys(pendingHods).length;

  const save = async () => {
    if (totalPending === 0) {
      toast.info('No changes to save.');
      return;
    }
    setSaving(true);
    try {
      const body = {
        entity_id: entityId,
        new_departments: pendingNewDepts,
        employee_assignments: Object.entries(pendingAssignments).map(([employee_id, department]) => ({ employee_id, department })),
        hod_assignments: Object.entries(pendingHods)
          .filter(([_, empId]) => !!empId)
          .map(([department, hod_employee_id]) => ({ department, hod_employee_id })),
      };
      const res = await fetch(`${API_URL}/api/departments/bulk-assign`, {
        method: 'POST',
        headers: headers(),
        credentials: 'include',
        body: JSON.stringify(body),
      });
      const d = await safeJson(res);
      if (!res.ok) throw new Error(d.detail || 'Save failed');
      const errCount = d.errors?.length || 0;
      const msg = `Saved — ${d.created} new dept(s), ${d.assigned} reassigned, ${d.hods_set} HOD(s) set` + (errCount ? `, ${errCount} error(s)` : '');
      if (errCount) toast.warning(msg);
      else toast.success(msg);
      if (errCount) {
        console.warn('Bulk-assign errors:', d.errors);
      }
      // Refresh from server
      fetchData();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!canManage) {
    return (
      <div className="p-6 text-center text-slate-500">
        Only HR / Admin can manage department mappings.
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in" data-testid="department-mapping-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="dm-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-slate-900 dark:text-white">Department Mapping</h1>
            <p className="text-xs text-slate-500">
              Assign employees to departments and set HODs in one place. All changes queue up until you click <strong>Save Changes</strong>.
            </p>
          </div>
        </div>
        <Button
          onClick={save}
          disabled={saving || totalPending === 0 || !entityId}
          className="bg-[#DC2626] hover:bg-[#B91C1C] text-white"
          data-testid="dm-save-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
          Save Changes{totalPending ? ` (${totalPending})` : ''}
        </Button>
      </div>

      {!entityId && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-3 text-sm text-amber-900">
            Please pick an Institute / Branch from the top selector before mapping departments.
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* LEFT: Departments overview + Create new */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Building2 className="w-4 h-4" /> Departments ({departments.length + pendingNewDepts.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="p-2 border-2 border-dashed rounded-md space-y-2">
              <p className="text-[10px] font-semibold uppercase text-slate-500">Add a new department</p>
              <Input
                placeholder="Department name (e.g. Diploma-Mechanical)"
                value={newDeptDraft.name}
                onChange={(e) => setNewDeptDraft({ ...newDeptDraft, name: e.target.value })}
                data-testid="dm-new-dept-name"
              />
              <Input
                placeholder="Short code (e.g. DMEC)"
                value={newDeptDraft.code}
                onChange={(e) => setNewDeptDraft({ ...newDeptDraft, code: e.target.value.toUpperCase() })}
                data-testid="dm-new-dept-code"
              />
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={onAddDraftDept}
                disabled={!newDeptDraft.name.trim()}
                data-testid="dm-add-new-dept-btn"
              >
                <Plus className="w-3 h-3 mr-1" /> Queue new department
              </Button>
            </div>

            <div className="space-y-2 max-h-[520px] overflow-y-auto">
              {allDeptNames.map(name => {
                const existing = departments.find(d => d.name === name);
                const isPendingNew = !existing && pendingNewDepts.some(d => d.name === name);
                const empsInDept = empsByDept[name] || [];
                const currentHod = currentHodEmployeeId(name);
                return (
                  <div key={name}
                    className={`p-2 rounded-md border ${isPendingNew ? 'bg-amber-50 border-amber-200' : 'bg-white border-slate-200'}`}
                    data-testid={`dm-dept-${name.replace(/\s+/g, '-')}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <div className="text-sm font-semibold flex items-center gap-1">
                          {name}
                          {isPendingNew && (
                            <Badge className="bg-amber-100 text-amber-700 text-[9px]">will create</Badge>
                          )}
                        </div>
                        <div className="text-[10px] text-slate-500">
                          {existing?.code || pendingNewDepts.find(d => d.name === name)?.code || '—'} · {empsInDept.length} staff
                        </div>
                      </div>
                    </div>
                    <div className="mt-2">
                      <Label className="text-[10px] uppercase tracking-wide text-slate-500 flex items-center gap-1">
                        <ShieldCheck className="w-3 h-3" /> HOD
                      </Label>
                      <Select
                        value={currentHod || ''}
                        onValueChange={(v) => onPickHod(name, v)}
                      >
                        <SelectTrigger className="h-8 mt-1" data-testid={`dm-hod-picker-${name.replace(/\s+/g, '-')}`}>
                          <SelectValue placeholder="— pick HOD from staff —" />
                        </SelectTrigger>
                        <SelectContent>
                          {empsInDept.map(e => (
                            <SelectItem key={e.employee_id} value={e.employee_id}>
                              {e.first_name} {e.last_name} ({e.designation || '—'})
                            </SelectItem>
                          ))}
                          {employees.filter(e => !empsInDept.some(d => d.employee_id === e.employee_id)).length > 0 && (
                            <SelectItem value="__divider__" disabled>— other departments&apos; staff —</SelectItem>
                          )}
                          {employees.filter(e => !empsInDept.some(d => d.employee_id === e.employee_id)).map(e => (
                            <SelectItem key={e.employee_id} value={e.employee_id}>
                              {e.first_name} {e.last_name} ({effectiveDeptForEmp(e) || 'No dept'})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-[9px] text-slate-400 mt-0.5">Same person can be HOD of multiple departments (e.g. Degree + Diploma)</p>
                      {existing && (
                        <div className="mt-1.5">
                          {existing.acting_hod_id ? (
                            <div className="flex items-center justify-between gap-1 bg-amber-50 border border-amber-200 rounded px-1.5 py-1">
                              <span className="text-[10px] text-amber-800">
                                Acting: <strong>{existing.acting_hod_name}</strong> till {existing.acting_to}
                              </span>
                              <Button variant="ghost" size="sm" className="h-5 px-1 text-[10px] text-red-600"
                                onClick={() => clearActingHod(existing)} data-testid={`dm-clear-acting-${name.replace(/\s+/g, '-')}`}>
                                End now
                              </Button>
                            </div>
                          ) : (
                            <Button variant="ghost" size="sm" className="h-6 px-1.5 text-[10px] text-blue-600"
                              onClick={() => { setActingDept(existing); setActingForm({ employee_id: '', from_date: '', to_date: '' }); }}
                              data-testid={`dm-acting-btn-${name.replace(/\s+/g, '-')}`}>
                              + Acting HOD for a period
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {allDeptNames.length === 0 && (
                <p className="text-xs text-slate-400 text-center py-4">
                  No departments yet — create one above or via employee row.
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* RIGHT: Employee mapping table */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Users className="w-4 h-4" /> Employees ({visibleEmployees.length} of {employees.length})
              </span>
              <div className="flex gap-2">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
                  <Input
                    placeholder="Search name / email / ID…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-7 h-8 w-56"
                    data-testid="dm-search"
                  />
                </div>
                <Select value={filterDept} onValueChange={setFilterDept}>
                  <SelectTrigger className="h-8 w-44" data-testid="dm-filter-dept">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All departments</SelectItem>
                    <SelectItem value="__none__">— unassigned —</SelectItem>
                    {allDeptNames.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {loading ? (
              <div className="text-center py-8 text-slate-500 text-sm">
                <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading…
              </div>
            ) : (
              <Table data-testid="dm-employees-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Designation</TableHead>
                    <TableHead>Current Dept</TableHead>
                    <TableHead>Assign To</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleEmployees.map(e => {
                    const current = e.department || '—';
                    const effective = effectiveDeptForEmp(e);
                    const changed = pendingAssignments[e.employee_id] !== undefined && pendingAssignments[e.employee_id] !== e.department;
                    return (
                      <TableRow key={e.employee_id} className={changed ? 'bg-amber-50/50' : ''} data-testid={`dm-emp-row-${e.employee_id}`}>
                        <TableCell>
                          <div className="text-sm font-medium">{e.first_name} {e.last_name}</div>
                          <div className="text-[10px] text-slate-500 font-mono">{e.employee_id} · {e.email}</div>
                        </TableCell>
                        <TableCell className="text-xs">{e.designation || '—'}</TableCell>
                        <TableCell className="text-xs">
                          <span className="text-slate-600">{current}</span>
                          {changed && (
                            <div className="text-[10px] text-amber-700 font-semibold">→ {effective}</div>
                          )}
                        </TableCell>
                        <TableCell>
                          <Select
                            value={effective || ''}
                            onValueChange={(v) => onPickDepartment(e.employee_id, v)}
                          >
                            <SelectTrigger className="h-8 w-56" data-testid={`dm-dept-picker-${e.employee_id}`}>
                              <SelectValue placeholder="— pick department —" />
                            </SelectTrigger>
                            <SelectContent>
                              {allDeptNames.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                              <SelectItem value={CREATE_NEW} className="text-emerald-700 font-semibold">
                                <Plus className="w-3 h-3 inline mr-1" /> Create new…
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {visibleEmployees.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-slate-400 py-6" data-testid="dm-empty">
                        No employees match the current filters.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Acting HOD Dialog */}
      <Dialog open={!!actingDept} onOpenChange={() => setActingDept(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Acting HOD — {actingDept?.name}</DialogTitle>
            <DialogDescription>
              Temporarily put someone in charge for a specific period. The current HOD is restored automatically after the end date.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-3">
            <div className="space-y-2">
              <Label>Acting HOD</Label>
              <Select value={actingForm.employee_id} onValueChange={(v) => setActingForm({ ...actingForm, employee_id: v })}>
                <SelectTrigger data-testid="dm-acting-emp"><SelectValue placeholder="Pick employee" /></SelectTrigger>
                <SelectContent>
                  {employees.map(e => (
                    <SelectItem key={e.employee_id} value={e.employee_id}>
                      {e.first_name} {e.last_name} ({effectiveDeptForEmp(e) || 'No dept'})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>From (blank = today)</Label>
                <Input type="date" value={actingForm.from_date} onChange={(e) => setActingForm({ ...actingForm, from_date: e.target.value })} data-testid="dm-acting-from" />
              </div>
              <div className="space-y-2">
                <Label>To *</Label>
                <Input type="date" value={actingForm.to_date} onChange={(e) => setActingForm({ ...actingForm, to_date: e.target.value })} data-testid="dm-acting-to" />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setActingDept(null)}>Cancel</Button>
              <Button className="bg-[#DC2626] hover:bg-[#B91C1C] text-white" onClick={saveActingHod} disabled={savingActing} data-testid="dm-acting-save">
                {savingActing && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}Set Acting HOD
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
